import { OnboardingState } from '@repo/domain/user';

const AI_IMAGEGEN_MODELS = 'AI_IMAGEGEN_MODELS';
const AI_COPILOT_STEP = 'AI_COPILOT_STEP';
const AI_ART_FEED = 'AI_ART_FEED';
const AI_IMAGE_TRANSFER_STYLE = 'AI_IMAGE_TRANSFER_STYLE';

export enum OnboardingVideoState {
  Start = 'VIDEO_START',
  Show = 'SHOW_VIDEO_MODAL',
}

export enum AnchorRefKeys {
  LEFT_MENU = 'leftMenu',
  STYLES_MENU = 'stylesMenu',

  ARTBOARD = 'artboard',

  TRANSFORMATION = 'transformationPanel',
  COLORS_PANEL = 'colorsPanel',
  DOWNLOAD_PANEL = 'downloadPanel',

  DECORATION_SLIDER = 'decorationSlider',
  SHADING_SLIDER = 'shadingSlider',

  TEMPLATES_BUTTON = 'templatesButton',
  ELEMENTS_BUTTON = 'elementsButton',
  AI_BUTTON = 'aiButton',
  COLORS_BUTTON = 'colorsButton',
  DOWNLOAD_BUTTON = 'downloadButton',
  PRINT_BUTTON = 'printButton',

  HELP = 'helpButton',
}

export interface ArtboardRef {
  top: number;
  left: number;
  topOnCanvas?: number;
  leftOnCanvas?: number;
  width: number;
  height: number;
}

// used by OnboardingManager
export type Refs = Record<
  (typeof AnchorRefKeys)[keyof typeof AnchorRefKeys],
  HTMLElement | undefined
> & { artboard?: ArtboardRef };

type OnboardingName =
  | OnboardingState
  | OnboardingVideoState
  | typeof AI_ART_FEED
  | typeof AI_IMAGE_TRANSFER_STYLE
  | typeof AI_IMAGEGEN_MODELS
  | typeof AI_COPILOT_STEP;

interface DescriptionItem {
  text: string;
  link?: string;
}

// used by OnboardingManager
export interface OnboardingStep {
  index?: number;
  description?: string | DescriptionItem[];
  dispatch?: {
    event: string; // Former DispatchEvent from editor
    value?: Record<string, unknown>;
  };
  refs?: AnchorRefKeys[];
  displayIndex?: number;
  skipLabel?: string | null;
  nextLabel?: string;
  label?: string;
  icon?: JSX.Element;
}

export interface OnboardingStepConfig {
  index: number;
  name: OnboardingName;
  steps: OnboardingStep[];
  isModal?: boolean;
  isSurvey?: boolean;
  label?: string;
  title?: string;
  fallback?: OnboardingStep;
  openMenu?:
    | 'designs'
    | 'template'
    | 'text'
    | 'elements'
    | 'upload'
    | 'photo'
    | 'texture'
    | 'mockup'
    | 'kittlAi'
    // close the panel
    | 'close';
  openRightMenu?: 'projectColors' | 'mockup' | 'styles' | 'effects';
  isLast?: boolean;
  isSkip?: boolean;
}

export interface DisplayStepConfig extends OnboardingStepConfig {
  currentSubStep: number;
}

export enum OnboardingKeys {
  base = 'base',
  video = 'video',
}

interface OnboardingInitParams {
  step?: OnboardingStepConfig;
  userId?: string;
}

export interface SurveyInitParams extends OnboardingInitParams {
  onboardingKey: OnboardingKeys | null;
}

export interface OnboardingStoreState {
  // Id of the user that is currently using the app
  initialisedForUser: string | null;

  // Current onboarding step
  step: OnboardingStepConfig | null;

  // individual steps may have sub steps. Their index is stored here.
  subStep: number;

  // Current onboarding step to display in the UI
  displayStep: DisplayStepConfig | null;

  // Current onboarding key
  onboardingKey: OnboardingKeys | null;

  // Collection of skipped onboardings
  skipped: OnboardingKeys[];

  // Flag that the store is already initialized
  initialized: boolean | Promise<void>;

  // Collection of refs
  refs: Refs;
  initHandler: (params: OnboardingInitParams) => Promise<void>;
  init: (
    params: OnboardingInitParams,
    forceReinitialization?: boolean
  ) => Promise<void>;
  setStep: (
    step: OnboardingStepConfig,
    subStep?: number,
    next?: boolean
  ) => Promise<void>;
  setNextStep: () => Promise<void>;
  setNextOnboarding: (step?: OnboardingStepConfig) => Promise<void>;
  setStepIfActive: (
    step: OnboardingStepConfig,
    subStep?: number
  ) => Promise<boolean | void>;
  registerStepRef: (ref: HTMLElement | ArtboardRef, step: string) => void;
  skipOnboarding: (keys?: OnboardingKeys[]) => Promise<void>;
  setSkipped: (keys: OnboardingKeys[]) => void;
  resetOnboarding: (data: {
    resetKey?: OnboardingKeys;
    gotoKey?: boolean;
    step?: OnboardingStepConfig;
  }) => Promise<void>;
  initializeStep: (
    step: OnboardingStepConfig,
    subStep?: number
  ) => Promise<void>;
}
